import type { Vec2 } from "../types";
import { cloneVec, length, sub } from "./math";
import { TrackRuntime } from "./track";

export interface ProgressSnapshot {
  totalProgress: number;
  maximumProgress: number;
  checkpoints: number;
  laps: number;
  reverseDistance: number;
  secondsWithoutProgress: number;
  rejectedProgressJumps: number;
}

export class ProgressEvaluator {
  readonly track: TrackRuntime;
  private lastTrackProgress = 0;
  private lastSegmentIndex = 0;
  private lastPosition: Vec2 = [0, 0];
  private totalProgress = 0;
  private maximumProgress = 0;
  private reverseDistance = 0;
  private checkpoints = 0;
  private laps = 0;
  private secondsWithoutProgress = 0;
  private rejectedProgressJumps = 0;

  constructor(track: TrackRuntime, position: Vec2) {
    this.track = track;
    this.reset(position);
  }

  reset(position: Vec2): void {
    const projection = this.track.project(position);
    this.lastTrackProgress = projection.progress;
    this.lastSegmentIndex = projection.segmentIndex;
    this.lastPosition = cloneVec(position);
    this.totalProgress = 0;
    this.maximumProgress = 0;
    this.reverseDistance = 0;
    this.checkpoints = 0;
    this.laps = 0;
    this.secondsWithoutProgress = 0;
    this.rejectedProgressJumps = 0;
  }

  reanchor(position: Vec2): void {
    const projection = this.track.projectNear(position, this.lastSegmentIndex);
    this.lastTrackProgress = projection.progress;
    this.lastSegmentIndex = projection.segmentIndex;
    this.lastPosition = cloneVec(position);
    this.secondsWithoutProgress = 0;
  }

  update(position: Vec2, dt: number, collided: boolean): ProgressSnapshot {
    const projection = this.track.projectNear(position, this.lastSegmentIndex);
    const current = projection.progress;
    const physicalDistance = length(sub(position, this.lastPosition));
    let delta = current - this.lastTrackProgress;
    if (delta > this.track.length / 2) delta -= this.track.length;
    else if (delta < -this.track.length / 2) delta += this.track.length;
    const onExpectedRoute = projection.distanceFromCenter <= projection.allowedHalfWidth + 1e-6;
    if (collided) {
      delta = 0;
    } else if (!onExpectedRoute || physicalDistance > 6) {
      delta = 0;
      this.rejectedProgressJumps += 1;
      if (onExpectedRoute) {
        this.lastTrackProgress = current;
        this.lastSegmentIndex = projection.segmentIndex;
      }
    } else {
      this.lastTrackProgress = current;
      this.lastSegmentIndex = projection.segmentIndex;
    }
    this.lastPosition = cloneVec(position);
    this.totalProgress += delta;
    this.maximumProgress = Math.max(this.maximumProgress, this.totalProgress);
    if (delta < 0) this.reverseDistance += -delta;
    if (delta > 0.12) this.secondsWithoutProgress = 0;
    else this.secondsWithoutProgress += dt;
    const spacing = this.track.length / 12;
    let nextThreshold = (this.checkpoints + 1) * spacing;
    while (this.totalProgress >= nextThreshold) {
      this.checkpoints += 1;
      if (this.checkpoints % 12 === 0) this.laps += 1;
      nextThreshold = (this.checkpoints + 1) * spacing;
    }
    return this.snapshot();
  }

  snapshot(): ProgressSnapshot {
    return {
      totalProgress: this.totalProgress,
      maximumProgress: this.maximumProgress,
      checkpoints: this.checkpoints,
      laps: this.laps,
      reverseDistance: this.reverseDistance,
      secondsWithoutProgress: this.secondsWithoutProgress,
      rejectedProgressJumps: this.rejectedProgressJumps,
    };
  }

  get stalled(): boolean {
    return this.secondsWithoutProgress >= 10;
  }

  get currentSegmentIndex(): number {
    return this.lastSegmentIndex;
  }
}
